import React, { useCallback, useMemo, useState } from 'react';
import { H3, ColumnProps, FlexItem, Table } from '@erecruit/ui-kit';
import { DataPreferencesSettings, Resources } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { fetchFacilityDepartments, LoadMoreSelect } from '@erecruit/modules';
import InfoContainer from './InfoContainer';

const { FormattedMessage } = Intl;

interface CompanySelectProps {
  companyId: number;
  onChange: (data: Record<number, number[]>) => void;
  defaultSelection: number[];
}

const CompanySelect = ({
  onChange,
  companyId,
  defaultSelection,
}: CompanySelectProps) => {
  const [selectedValues, setSelectedValues] = useState<number[]>([]);
  const onSelectChange = useCallback(
    (selectedValues: number[]) => {
      onChange({ [companyId]: selectedValues });
      setSelectedValues(selectedValues);
    },
    [companyId, onChange],
  );

  const injectFilter = useCallback(() => {
    return {
      companyIds: [companyId],
    };
  }, [companyId]);

  return (
    <InfoContainer
      messageId={messageIds.vms.settings.data.emptyPositionTypesWarning}
      displayInfo={selectedValues.length === 0}
    >
      <LoadMoreSelect
        fetchEntities={fetchFacilityDepartments}
        onChange={onSelectChange}
        injectFilter={injectFilter}
        defaultSelection={defaultSelection}
        getDefaultSelectionFilter={() => ({ ids: defaultSelection })}
      />
    </InfoContainer>
  );
};

interface CompaniesPickerProps {
  dataSettings: DataPreferencesSettings;
  companies: Resources.Company[];
  onChange: (changedSelection: Record<number, number[]>) => void;
}

export const CompaniesPicker = ({
  companies,
  onChange,
  dataSettings,
}: CompaniesPickerProps) => {
  const onCompanySelectChange = useCallback(
    (data: Record<number, number[]>) => {
      onChange(data);
    },
    [onChange],
  );

  const columns: ColumnProps<{}>[] = useMemo(
    () => [
      {
        key: 'name',
        dataIndex: 'name',
        title: 'Company',
        width: '30%',
        render: name => <H3>{name}</H3>,
      },
      {
        key: 'departments',
        dataIndex: 'id',
        title: 'Departments',
        width: '70%',
        render: id => (
          <CompanySelect
            onChange={onCompanySelectChange}
            companyId={id}
            defaultSelection={
              dataSettings.companies.find(company => company.companyId === id)
                ?.departmentIds || []
            }
          />
        ),
      },
    ],
    [onCompanySelectChange, dataSettings.companies],
  );

  return (
    <FlexItem gutter={2} gutterDirection="vertical">
      <H3 weight={400}>
        <FormattedMessage
          id={messageIds.vms.settings.data.companiesFilterTitle}
        />
      </H3>
      <Table
        columns={columns}
        dataSource={companies}
        alwaysVisibleColumns={['company', 'departments']}
        pagination={false}
        columnSelector={false}
      />
    </FlexItem>
  );
};
